import { useNavigate } from "react-router-dom";
import { Reveal } from "@/components/public/Reveal";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, MapPin, ShieldCheck, Truck, Users, AlertTriangle, ClipboardCheck,
  Wrench, Camera, Activity, Clock, Navigation, BarChart3, CheckCircle2, Play,
} from "lucide-react";

const IMG = {
  fleet: "https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  wheelchair: "https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ops: "https://images.unsplash.com/photo-1708807472445-d33589e6b090?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const FEATURES = [
  { icon: Users, title: "Driver Management", desc: "Rosters, credentials, performance and real-time duty status for every driver." },
  { icon: Navigation, title: "Dispatch & Scheduling", desc: "Build schedules, assign and reassign trips, and keep operations moving." },
  { icon: MapPin, title: "Live GPS Visibility", desc: "See every vehicle on the map in real time with trip status at a glance." },
  { icon: Clock, title: "Late-Trip Prevention", desc: "Proactive late-risk alerts flag trips before they slip so you can intervene." },
  { icon: AlertTriangle, title: "Incident Management", desc: "Log, categorize and resolve incidents with a clear accountability trail." },
  { icon: Wrench, title: "Fleet Maintenance", desc: "Track service, repairs and repair costs across the entire fleet." },
  { icon: ShieldCheck, title: "Driver & Vehicle Compliance", desc: "Monitor licenses, inspections and certifications before they expire." },
  { icon: Camera, title: "Trip Evidence", desc: "Capture and retain proof for every ride to resolve disputes with confidence." },
];

const STEPS = [
  { n: "01", title: "Onboard your fleet", desc: "Add drivers, vehicles and compliance records in minutes." },
  { n: "02", title: "Dispatch & track live", desc: "Schedule trips, assign drivers and watch operations unfold on the live map." },
  { n: "03", title: "Prevent & document", desc: "Catch late-risk trips early, log incidents and capture trip evidence." },
  { n: "04", title: "Report & account", desc: "Review performance, repair costs and compliance across the operation." },
];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`mx-auto max-w-7xl px-5 lg:px-8 ${className}`}>{children}</section>;
}

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-slate-950 pt-28 pb-24 lg:pt-36 lg:pb-32">
        <div className="absolute inset-0">
          <img src={IMG.fleet} alt="Transportation fleet" className="h-full w-full object-cover opacity-45 brightness-125 contrast-105" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/75 to-blue-950/55" />
        </div>
        <Section className="relative">
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <Reveal className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
                <span className="relative flex h-2 w-2"><span className="rs-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
                Operations & Accountability Platform
              </Reveal>
              <Reveal as="h1" delay={0.08}
                className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Every ride. Every driver.<br />
                <span className="text-amber-500">Every vehicle. Accounted for.</span>
              </Reveal>
              <Reveal as="p" delay={0.16}
                className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
                RideStamp is the operations and accountability platform built for non-emergency and
                non-medical transportation providers — manage drivers, trips, dispatch, live GPS,
                compliance and incidents in one place.
              </Reveal>
              <Reveal delay={0.24} className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => navigate("/request-demo")} data-testid="hero-request-demo"
                  className="group gap-2 rounded-full bg-blue-600 px-7 py-6 text-base font-semibold text-white hover:bg-blue-500">
                  Request a Demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button onClick={() => navigate("/login")} variant="outline" data-testid="hero-login"
                  className="gap-2 rounded-full border-slate-600 bg-white/5 px-7 py-6 text-base font-semibold text-white hover:bg-white/10 hover:text-white">
                  <Play className="h-4 w-4" /> See RideStamp in Action
                </Button>
              </Reveal>
              <Reveal delay={0.32} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
                {["Live GPS tracking", "Late-trip prevention", "Full audit trail"].map((t) => (
                  <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t}</span>
                ))}
              </Reveal>
            </div>

            {/* Live preview mock */}
            <Reveal delay={0.2}
              className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-4 shadow-2xl backdrop-blur">
              <div className="flex items-center justify-between border-b border-slate-700/60 pb-3">
                <span className="font-mono-data text-xs text-slate-400">LIVE OPERATIONS</span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                  <span className="relative flex h-2 w-2"><span className="rs-ping absolute h-2 w-2 rounded-full bg-emerald-400" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
                  8 vehicles
                </span>
              </div>
              <div className="relative mt-3 h-52 overflow-hidden rounded-xl bg-[#0b1120]"
                style={{ backgroundImage: "linear-gradient(#162033 1px,transparent 1px),linear-gradient(90deg,#162033 1px,transparent 1px)", backgroundSize: "28px 28px" }}>
                {[["18%", "30%", "bg-emerald-400"], ["55%", "48%", "bg-amber-400"], ["72%", "22%", "bg-emerald-400"], ["38%", "68%", "bg-red-400"], ["82%", "60%", "bg-emerald-400"]].map(([l, t, c], i) => (
                  <span key={i} className={`absolute h-3 w-3 rounded-full ${c} shadow-lg`} style={{ left: l, top: t, animation: `rs-drift ${3 + i}s ease-in-out infinite` }} />
                ))}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[["On-time", "92%", "text-emerald-400"], ["Late-risk", "2", "text-amber-400"], ["Active", "6", "text-blue-400"]].map(([k, v, c]) => (
                  <div key={k} className="rounded-lg bg-slate-800/60 p-2.5 text-center">
                    <div className={`font-mono-data text-lg font-bold ${c}`}>{v}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </Section>
      </div>

      {/* Trust bar */}
      <div className="border-y border-slate-200 bg-slate-50 py-6">
        <Section className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Purpose-built for</p>
          {["NEMT Providers", "Paratransit", "Senior Transport", "Dialysis Routes", "Agency Fleets"].map((t) => (
            <span key={t} className="font-display text-sm font-bold text-slate-500">{t}</span>
          ))}
        </Section>
      </div>

      {/* Features */}
      <Section id="features" className="py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-700">Capabilities</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">
            One platform for the entire operation
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Everything your team needs to run a reliable, accountable transportation operation — with nothing getting lost between systems.
          </p>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 4) * 0.06}
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl hover:shadow-slate-200/60"
              data-testid={`feature-card-${i}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700 transition-colors group-hover:bg-blue-800 group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <div id="how-it-works" className="bg-slate-950 py-20 text-white lg:py-28">
        <Section>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">How it works</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight lg:text-4xl">From dispatch to documented in four steps</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06} className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-7">
                <span className="font-mono-data text-4xl font-bold text-blue-500/30">{s.n}</span>
                <h3 className="mt-3 font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* Fleet & Drivers split */}
      <Section id="fleet" className="py-20 lg:py-28">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-700">Fleet & Driver Management</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">
              Keep every driver and vehicle ready to roll
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Maintain a single source of truth for your fleet — duty status, assignments, mileage,
              maintenance and repair costs — alongside driver rosters, ratings and credentials.
            </p>
            <ul className="mt-7 space-y-4">
              {[
                [Truck, "Full vehicle roster with status, type and inspection tracking"],
                [Wrench, "Maintenance history and repair cost tracking per vehicle"],
                [Users, "Driver rosters, ratings, and real-time duty status"],
                [ClipboardCheck, "Expiring-credential alerts before they put you at risk"],
              ].map(([Icon, t]) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Icon className="h-4 w-4" /></span>
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={IMG.wheelchair} alt="Accessible transport van" className="rounded-2xl shadow-2xl shadow-slate-300/60" />
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><ShieldCheck className="h-5 w-5" /></span>
                <div>
                  <div className="font-mono-data text-lg font-bold text-slate-900">98.6%</div>
                  <div className="text-xs text-slate-500">Compliance up to date</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Safety & accountability */}
      <div id="safety" className="bg-slate-50 py-20 lg:py-28">
        <Section>
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <img src={IMG.ops} alt="Operations control room" className="rounded-2xl shadow-2xl shadow-slate-300/60" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-amber-600">Safety & Accountability</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">
                Proof, not guesswork
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                When something goes wrong, you need answers fast. RideStamp keeps a clear,
                time-stamped record of every trip, incident and resolution — so your operation
                stays accountable to riders, agencies and auditors alike.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  [Activity, "Late-trip prevention", "Risk alerts before trips slip"],
                  [AlertTriangle, "Incident management", "Logged, categorized, resolved"],
                  [Camera, "Trip evidence", "Documented proof for every ride"],
                  [BarChart3, "Operational reporting", "Clear accountability metrics"],
                ].map(([Icon, t, d]) => (
                  <div key={t} className="rounded-xl border border-slate-200 bg-white p-5">
                    <Icon className="h-5 w-5 text-blue-700" />
                    <h4 className="mt-3 font-display text-base font-bold text-slate-900">{t}</h4>
                    <p className="mt-1 text-sm text-slate-500">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* CTA */}
      <Section className="py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 to-slate-950 px-8 py-16 text-center lg:px-16 lg:py-20">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #3b82f6 0, transparent 40%)" }} />
          <div className="relative mx-auto max-w-2xl">
            <h2 className="font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">
              Ready to account for every ride?
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              See how RideStamp brings your drivers, trips, fleet and compliance into one accountable operation.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button onClick={() => navigate("/request-demo")} data-testid="cta-request-demo"
                className="gap-2 rounded-full bg-blue-600 px-8 py-6 text-base font-semibold text-white hover:bg-blue-500">
                Request a Demo <ArrowRight className="h-4 w-4" />
              </Button>
              <Button onClick={() => navigate("/contact")} variant="outline" data-testid="cta-talk-sales"
                className="rounded-full border-slate-600 bg-transparent px-8 py-6 text-base font-semibold text-white hover:bg-white/10 hover:text-white">
                Talk to Sales
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
